/**
 * Conversion Analytics Dashboard
 * Shows where users are dropping off in the signup/onboarding funnel
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, Users, Building2, CreditCard, 
  AlertTriangle, CheckCircle, XCircle, ArrowRight, RefreshCw,
  Eye, UserPlus, FileText, DollarSign
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ConversionAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/conversion-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-700">{error || 'No analytics data available'}</p>
          <Button onClick={fetchAnalytics} className="mt-4">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const funnelSteps = [
    { 
      name: 'Page Views', 
      count: analytics.page_views || 0, 
      icon: Eye, 
      color: 'bg-blue-500',
      description: 'Total website visitors'
    },
    { 
      name: 'Signups', 
      count: analytics.signups || 0, 
      icon: UserPlus, 
      color: 'bg-indigo-500',
      description: 'Users who created accounts'
    },
    { 
      name: 'Organizations', 
      count: analytics.organizations || 0, 
      icon: Building2, 
      color: 'bg-purple-500',
      description: 'Started onboarding'
    },
    { 
      name: 'Payment Attempts', 
      count: analytics.payment_attempts || 0, 
      icon: CreditCard, 
      color: 'bg-orange-500',
      description: 'Tried to pay'
    },
    { 
      name: 'Completed Sales', 
      count: analytics.completed_sales || 0, 
      icon: DollarSign, 
      color: 'bg-emerald-500',
      description: 'Successful conversions'
    }
  ];

  // Calculate drop-off rates
  const calculateDropOff = (current, previous) => {
    if (previous === 0) return 0;
    return Math.round((1 - current / previous) * 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Conversion Funnel Analytics</h2>
          <p className="text-gray-500">Track where potential customers are dropping off</p>
        </div>
        <Button onClick={fetchAnalytics} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Funnel Visualization */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Funnel</CardTitle>
          <CardDescription>Visual representation of user journey</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-2">
            {funnelSteps.map((step, index) => {
              const Icon = step.icon;
              const dropOff = index > 0 ? calculateDropOff(step.count, funnelSteps[index - 1].count) : 0;
              const isBottleneck = dropOff > 50;
              
              return (
                <React.Fragment key={step.name}>
                  <div className="flex-1 text-center">
                    <div className={`${step.color} text-white rounded-lg p-4 mb-2 ${isBottleneck ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}>
                      <Icon className="h-6 w-6 mx-auto mb-2" />
                      <div className="text-2xl font-bold">{step.count.toLocaleString()}</div>
                      <div className="text-xs opacity-90">{step.name}</div>
                    </div>
                    {index > 0 && dropOff > 0 && (
                      <Badge variant={isBottleneck ? "destructive" : "secondary"} className="text-xs">
                        {dropOff}% drop-off
                      </Badge>
                    )}
                  </div>
                  {index < funnelSteps.length - 1 && (
                    <ArrowRight className="h-6 w-6 text-gray-300 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Problem Areas */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Users Without Organizations */}
        <Card className={analytics.users_without_org > 10 ? 'border-red-300 bg-red-50' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {analytics.users_without_org > 10 ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              Post-Signup Drop-off
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-red-600 mb-2">
              {analytics.users_without_org || 0}
            </div>
            <p className="text-gray-600">
              Users who signed up but never started onboarding
            </p>
            {analytics.users_without_org > 10 && (
              <div className="mt-4 p-3 bg-white rounded border border-red-200">
                <p className="text-sm font-medium text-red-700">⚠️ Action Needed:</p>
                <p className="text-sm text-red-600">
                  Consider adding email reminders or simplifying the signup-to-onboarding transition
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Failures */}
        <Card className={analytics.failed_payments > 5 ? 'border-red-300 bg-red-50' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {analytics.failed_payments > 5 ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              Payment Failures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-red-600 mb-2">
              {analytics.failed_payments || 0}
            </div>
            <p className="text-gray-600">
              Payment attempts that failed ({analytics.payment_failure_rate || 0}% failure rate)
            </p>
            {analytics.failed_payments > 5 && (
              <div className="mt-4 p-3 bg-white rounded border border-red-200">
                <p className="text-sm font-medium text-red-700">⚠️ Critical Issue:</p>
                <p className="text-sm text-red-600">
                  High payment failure rate. Check Stripe integration and error handling.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Incomplete Onboarding */}
        <Card className={analytics.incomplete_onboarding > 5 ? 'border-yellow-300 bg-yellow-50' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {analytics.incomplete_onboarding > 5 ? (
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              Incomplete Onboarding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-yellow-600 mb-2">
              {analytics.incomplete_onboarding || 0}
            </div>
            <p className="text-gray-600">
              Organizations stuck in onboarding process
            </p>
            {analytics.onboarding_steps && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium">Stuck at step:</p>
                {Object.entries(analytics.onboarding_steps).map(([step, count]) => (
                  <div key={step} className="flex justify-between text-sm">
                    <span>Step {step}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Overall Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-emerald-600 mb-2">
              {analytics.conversion_rate || 0}%
            </div>
            <p className="text-gray-600">
              From page view to completed sale
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Page View → Signup</span>
                <span>{analytics.signup_rate || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span>Signup → Organization</span>
                <span>{analytics.org_rate || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span>Organization → Payment</span>
                <span>{analytics.payment_rate || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span>Payment → Success</span>
                <span>{analytics.success_rate || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Failed Payments */}
      {analytics.recent_failed_payments && analytics.recent_failed_payments.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-700">Recent Failed Payments</CardTitle>
            <CardDescription>These users tried to pay but failed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.recent_failed_payments.map((payment, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium">{payment.email || 'Unknown'}</p>
                    <p className="text-sm text-gray-500">{payment.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">${payment.amount}</p>
                    <Badge variant="destructive">{payment.error || 'Failed'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-blue-700">💡 Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {analytics.users_without_org > 10 && (
              <li className="flex items-start gap-2">
                <Badge variant="destructive">High Priority</Badge>
                <span>Send reminder emails to users who signed up but haven't started onboarding</span>
              </li>
            )}
            {analytics.failed_payments > 5 && (
              <li className="flex items-start gap-2">
                <Badge variant="destructive">Critical</Badge>
                <span>Review Stripe integration - 60% payment failure rate needs immediate attention</span>
              </li>
            )}
            {analytics.incomplete_onboarding > 5 && (
              <li className="flex items-start gap-2">
                <Badge className="bg-yellow-500">Medium</Badge>
                <span>Consider simplifying onboarding steps or adding progress save functionality</span>
              </li>
            )}
            <li className="flex items-start gap-2">
              <Badge variant="outline">Suggestion</Badge>
              <span>Add exit-intent popups and abandoned cart emails to recover lost conversions</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversionAnalytics;
